import { UserCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { RowActionButton } from '@/components/shared/list-row-actions';
import { IMPERSONATE_SCOPE, useImpersonation } from '@/features/impersonation/use-impersonation';
import { usePermissions } from '@/lib/use-permissions';

/**
 * SOR-AKCIÓ: „belépés ennek a személynek a nevében" — KIZÁRÓLAG a KAPCSOLATTARTÓK (partner) listán.
 *
 * A Munkatársak lapon SZÁNDÉKOSAN nincs: a megszemélyesítés partner-only, az Odin a belsős célt
 * `impersonation_target_not_partner` 403-cal utasítja el, tehát a gomb ott hamis affordancia lenne. Az ok a
 * VÉDETT jogok (per-user kiosztás, szerephez nem köthető — ma a Bevétel/kiadás hármas): egy birtokos nevében
 * belépve a „csak birtokostól, magadnak soha" fal megkerülhető lenne.
 *
 * ══ A JOG-KAPU: EGY kulcs ══════════════════════════════════════════════════════════════════════════════
 * A kapu a hívó tokenjének `scope` claimjében az `iam:impersonate` — ez a „Megszemélyesítés (belépés más
 * nevében)" pipa a jog-konzol „Adminisztráció" csoportjában. A kulcs MAGA a platform-képesség (nincs mellé
 * külön feature-jog), tehát amit az admin bepipál, az tényleg működik. Ugyanez a minta, mint a
 * munkatárs-felvétel `iam:provision` gombjánál: a `permissions` claim helyett a `scope` claim a kapu.
 *
 * ══ JOG NÉLKÜL: TILTOTT GOMB, NEM ELTÜNTETETT ══════════════════════════════════════════════════════════
 * A repo UI-konvenciója (`docs/foundations/design.md`): MENÜPONT jog nélkül elrejtve, MŰVELET/GOMB/INPUT
 * viszont `disabled` + magyarázó tooltip. Így a felhasználó látja, hogy a funkció LÉTEZIK, és azt is, miért
 * nem használhatja — a néma eltűnés helyett. (korábban itt eltűnt a gomb; a lelet ennél rosszabb volt:
 * platform-adminnál AKTÍV gomb látszott, ami 403-ba futott — lásd `use-permissions.ts` exact-match halmaz.)
 *
 * ══ AMIT A GOMB NEM TUD ELDÖNTENI ══════════════════════════════════════════════════════════════════════
 * Hogy a CÉL megszemélyesíthető-e (nem admin, nem gépi, aktív, tagja az appnak) — azt a platform dönti el, és
 * a hiba beszédes 403/422 lesz. Ezt SZÁNDÉKOSAN nem próbáljuk a kliensen megtippelni: a lista nem tudja, ki
 * milyen jogot hordoz (az emelt jog kereszt-app vizsgálat), és egy téves tiltás rosszabb, mint egy magyarázó hiba.
 */
export function ImpersonateRowAction({
  identityId,
  name,
  /** A saját sor: önmagát senki nem személyesítheti meg (a szerver 422-t ad) — ott a gombot elrejtjük. */
  isSelf = false,
}: {
  identityId: string;
  name: string;
  isSelf?: boolean;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { hasScope, isLoading } = usePermissions();
  const { start, isPending } = useImpersonation();

  // A SAJÁT sor az EGYETLEN eset, ahol a művelet nem is értelmezhető (a szerver 422-t ad) → ott nincs gomb.
  if (isSelf) return null;

  const allowed = hasScope(IMPERSONATE_SCOPE);

  return (
    <RowActionButton
      label={t('impersonation.rowAction', { name })}
      icon={UserCircleIcon}
      disabled={isLoading || !allowed || isPending}
      // A JOG-HIÁNY magyarázata csak akkor megy ki, ha TUDJUK, hogy nincs jog. A session-betöltés alatt a
      // gomb tiltott, de a tooltip a sima címke — különben egy pillanatra „nincs jogosultságod" villanna
      // annak is, akinek van.
      // `exactOptionalPropertyTypes` miatt a prop-ot NEM `undefined`-del adjuk át, hanem el sem adjuk.
      {...(allowed || isLoading ? {} : { disabledTooltip: t('impersonation.noPermission') })}
      onClick={() => void start(identityId)}
    />
  );
}
